import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Space, Switch, Typography } from "antd";
import { getAllFeatureFlags, updateFeatureFlagsValue, Props } from "api/Features";

const { Text } = Typography;

type FlagName = "ozon_stock_management" | "wb_stock_management";

const FLAG_META: Record<FlagName, { title: string; description: string }> = {
  ozon_stock_management: {
    title: "Ozon: sync в remnants_pipeline",
    description: "Управляет load/send шагами Ozon в remnants_pipeline.",
  },
  wb_stock_management: {
    title: "WB: sync в remnants_pipeline",
    description: "Управляет load/send шагами WB в remnants_pipeline.",
  },
};

export const StockManagementControls: React.FC = () => {
  const [flags, setFlags] = useState<Record<FlagName, boolean> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<FlagName | null>(null);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await getAllFeatureFlags();
      const mapByName = new Map(all.map((f: Props) => [f.name, f.value]));
      setFlags({
        ozon_stock_management: Boolean(mapByName.get("ozon_stock_management")),
        wb_stock_management: Boolean(mapByName.get("wb_stock_management")),
      });
    } catch (e) {
      setError("Не удалось загрузить feature flags для stock_management.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const toggleFlag = useCallback(
    async (name: FlagName, value: boolean) => {
      const prev = flags;
      if (!prev) return;
      setSaving(name);
      setError(null);
      setFlags({ ...prev, [name]: value });
      try {
        await updateFeatureFlagsValue(name, value);
      } catch (e) {
        setFlags(prev);
        setError(`Не удалось обновить флаг ${name}.`);
      } finally {
        setSaving(null);
      }
    },
    [flags]
  );

  const items = useMemo(
    () =>
      (Object.keys(FLAG_META) as FlagName[]).map((name) => ({
        name,
        ...FLAG_META[name],
      })),
    []
  );

  return (
    <Card
      title="Управление синком резервов"
      style={{ marginTop: 16 }}
      extra={
        <Button size="small" onClick={loadFlags} loading={loading}>
          Обновить
        </Button>
      }
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {items.map((item) => (
          <Card key={item.name} size="small">
            <Space
              align="center"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <div>
                <div>{item.title}</div>
                <Text type="secondary">{item.description}</Text>
              </div>
              <Switch
                checked={Boolean(flags?.[item.name])}
                onChange={(checked) => toggleFlag(item.name, checked)}
                loading={saving === item.name}
                disabled={loading}
              />
            </Space>
          </Card>
        ))}
      </Space>
    </Card>
  );
};

